import { useMemo, useRef } from "react";
import { PerformanceDatapoint } from "owa-analytics";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { trace } from "owa-trace";
import type { IframeState } from "./useIframeState";
import type { IframeScenario } from "../settings/iframes-mapping";
import { addIframeOnLoadMetric } from "../metrics/iframeMetrics";
import { getIframeMemoryInfo, getIframeFullInfo } from "../utils/iframeMemoryService";

type LoadTypes = "initial" | "session";

export interface PerfEventHandler {
    dp: PerformanceDatapoint | null;
    dpLoadStart: (loadType: LoadTypes) => void;
    dpLoadEnd: (src: string, state: IframeState, preload: boolean, memoryFromNativeHost?: number) => Promise<void>;
    dpEndEvent: () => void;
    hasStarted: boolean;
}

interface PerfEvents {
    getPerfEvent: (iframeId: string) => PerfEventHandler;
}

/**
 * Hook to manage the iframe PerformanceDatapoint.
 * Custom options:
 * - type: The type of the iframe (inline or wrapper)
 * - scenario: The scenario in which the iframe is used (ex: bizchat)
 * Waterfall:
 * - 1: loading start
 * - 2: iframe loaded
 */
export function useIframePerfEvents(type: "inline" | "wrapper", // Should not change
scenario: IframeScenario // Should not change
): PerfEvents {
    const perfEvents = useRef<Map<string, PerformanceDatapoint>>(new Map<string, PerformanceDatapoint>());
    
    const getPerfEventWrapper = useMemo(() => ({
        getPerfEvent: (iframeDomId: string): PerfEventHandler => {
            let perfEvent = perfEvents.current.get(iframeDomId);
            const exist = !!perfEvent;
            
            if (!perfEvent) {
                // Using getGlobalSettingsAccountMailboxInfo: Iframe performance tracking is app-level
                // infrastructure telemetry. The iframe wrapper hosts various content that may span
                // multiple accounts and doesn't have specific account context.
                perfEvent = new PerformanceDatapoint("IframeWrapper", getGlobalSettingsAccountMailboxInfo());
                perfEvent.addCustomData({
                    type,
                    scenario,
                });
                trace.info(`[DATAPOINT] IframeWrapper - Iframe performance event created (ID: ${iframeDomId}): ${JSON.stringify(perfEvent)}`, "iframeWrapper");
                perfEvents.current.set(iframeDomId, perfEvent);
            }
            
            return {
                dp: perfEvent ?? null,
                dpLoadStart: (loadType: LoadTypes) => {
                    perfEvent?.addToCustomWaterfall(1, "iframeLoading");
                    perfEvent?.addCustomData({
                        loadType,
                    });
                },
                dpLoadEnd: async (src: string, state: IframeState, preload: boolean, memoryFromNativeHost?: number) => {
                    perfEvent?.addToCustomWaterfall(2, "iframeLoaded");
                    perfEvent?.addCustomData({
                        state,
                        preload,
                    });
                    
                    // IMPORTANT: This currently tracks the total memory for all iframes matching the scenario
                    // We will eventually change it to track per-iframe using query parameters with unique IDs.
                    const memory = memoryFromNativeHost ??
                        (await getIframeMemoryInfo(scenario))?.totalMemoryMb;
                    
                    trace.info(` Iframe ${src} for scenario ${scenario} loaded with memory: ${memory}`, "iframeWrapper");
                    perfEvent?.addCustomData({
                        memoryOnLoad: memory ?? -1,
                    });
                    
                    // Update the iframe 'onload' metrics
                    const startTime = perfEvent?.getStartTime();
                    const timeElapsed = perfEvent && typeof startTime === "number"
                        ? Date.now() - startTime
                        : -1;
                    addIframeOnLoadMetric("global", memory ?? -1, timeElapsed);
                    if (scenario === "BizChatSidebar") {
                        addIframeOnLoadMetric("copilot", memory ?? -1, timeElapsed);
                    }
                },
                dpEndEvent: () => {
                    perfEvent?.addCustomData({ ...getIframeFullInfo(scenario) });
                    trace.info(`[DATAPOINT] IframeWrapper - Iframe performance event ended (ID: ${iframeDomId}): ${JSON.stringify(perfEvent)}`, "iframeWrapper");
                    perfEvent?.end();
                },
                hasStarted: exist,
            };
        },
    }), [scenario, type]);
    
    return getPerfEventWrapper;
}
